import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";

const HIDDEN_ON = ["/checkout", "/auth"];

export default function CartReminder() {
  const { items, count, subtotal, setOpen } = useCart();
  const { format } = useCurrency();
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("atw_cart_reminded")) return undefined;
    if (!count || HIDDEN_ON.includes(pathname)) return undefined;
    const t = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(t);
  }, [count, pathname]);

  const dismiss = () => {
    setShow(false);
    sessionStorage.setItem("atw_cart_reminded", "1");
  };
  const resume = () => {
    dismiss();
    setOpen(true);
  };

  const preview = items.slice(0, 3);

  return (
    <AnimatePresence>
      {show && count > 0 && !HIDDEN_ON.includes(pathname) && (
        <motion.aside
          data-testid="cart-reminder"
          initial={{ opacity: 0, y: 30, x: -10 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-5 left-5 z-[120] w-[300px] max-w-[calc(100vw-2.5rem)] bg-obsidian text-ivory border border-gold/30 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)]"
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_0%,rgba(212,175,55,0.18),transparent_60%)] pointer-events-none" />
          <button
            data-testid="cart-reminder-close"
            onClick={dismiss}
            aria-label="Dismiss"
            className="absolute top-2.5 right-2.5 z-10 p-1.5 text-ivory/45 hover:text-gold-light transition-colors"
          >
            <X size={14} />
          </button>
          <div className="relative p-5">
            <p className="text-[9px] tracking-[0.4em] uppercase text-gold-light">Your cart is saved</p>
            <p className="mt-2 font-serif text-lg leading-tight">
              Pick up where you left off — {count} piece{count === 1 ? "" : "s"} waiting.
            </p>
            <div className="mt-4 flex items-center gap-2">
              {preview.map((i) => (
                <div key={i.key} className="w-11 h-14 shrink-0 overflow-hidden bg-coal border border-gold/15">
                  <img src={i.image} alt={i.name} loading="lazy" className="h-full w-full object-cover" />
                </div>
              ))}
              {count > preview.length && (
                <span className="text-[10px] tracking-[0.2em] text-ivory/50">+{count - preview.length}</span>
              )}
            </div>
            <div className="mt-4 flex items-center justify-between gap-3">
              <span className="text-[10px] tracking-[0.25em] uppercase text-ivory/50">
                {format(subtotal)}
              </span>
              <button
                data-testid="cart-reminder-resume"
                onClick={resume}
                className="bg-gold-light text-ink px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase font-medium hover:bg-ivory transition-colors"
              >
                Resume
              </button>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
